import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Plus } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, type as text, pageSubtitle } from "../../theme";
import { RulerProgress } from "../../components/Drawing";
import { api, apiErrorMessage } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { LoadingBlock } from "../../components/Spinner";
import { PortalLink } from "../../components/PortalLink";
import { EntityTagsRow } from "../../components/EntityTags";
import { tableHeadRow,
  ModalOverlay, ModalHeader, Field, MultiPick, EmptyState, SeverityPill, input, textarea, secondaryBtn, dangerBtn, fmtDay, relativeDue,
  type AttentionItem, type Option,
} from "./shared";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../../components/RecordView";
import { PlaybookBar } from "../../components/PlaybookBar";
import { NextSteps, type NextStepItem } from "../../components/NextSteps";

// Business impact analysis — one analysis per business process or service: how badly its loss
// hurts as an outage grows, and the recovery objectives that follow. Readiness is computed by the
// API (services/businessImpact.ts); the impact curve is also computed here, live, while editing.

interface Props { canEdit: boolean; onChanged: () => void }

interface Dimension { id: string; label: string; help: string }
interface Interval { id: string; label: string; hours: number }
interface Level { level: number; label: string }
interface Vocabulary { dimensions: Dimension[]; intervals: Interval[]; levels: Level[]; tiers: Record<string, string>; statuses: string[] }

type Matrix = Record<string, Record<string, number>>;

interface ReadinessGap { kind: string; subject: string; message: string; severity: "high" | "medium" | "low" }
interface Dependency { id: string; name: string; depth: number; rtoHours: number | null; contingencyPlan: boolean; contingencyTestedAt: string | null; status: string }

interface Analysis {
  id: string;
  displayId: number | null;
  name: string;
  description: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  owner: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
  serviceAssetId: string | null;
  serviceAsset: { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean } | null;
  status: "draft" | "approved" | "archived";
  impacts: Matrix;
  financialLossPerHour: number | null;
  currency: string;
  mtpdHours: number | null;
  rtoHours: number | null;
  rpoHours: number | null;
  minimumServiceLevel: string | null;
  peakPeriods: string | null;
  workarounds: string | null;
  workaroundMaxHours: number | null;
  keyPeople: string | null;
  regulatoryNotes: string | null;
  reviewIntervalMonths: number;
  approvedAt: string | null;
  lastReviewedAt: string | null;
  nextReviewAt: string | null;
  assets: Array<{ assetId: string; asset: { id: string; name: string; kind: string; rtoHours: number | null } }>;
  suppliers: Array<{ supplierId: string; supplier: { id: string; name: string; status: string } }>;
  derived: {
    tier: 1 | 2 | 3 | 4;
    suggestedMtpdHours: number | null;
    curve: Array<{ interval: string; hours: number; level: number; drivers: string[] }>;
    toleranceIssues: Array<{ field: string; message: string }>;
    readiness: { score: number; checks: number; passed: number; gaps: ReadinessGap[]; slowestRecoveryHours: number | null };
    /** Suppliers' restore commitments and the last restore test against the RTO / RPO (lib/businessImpact.ts recoveryCommitments). */
    recovery?: RecoveryCommitments;
    lastExercisedAt: string | null;
    dependencies: Dependency[];
  };
  /** Incidents (open, or in the last year) that took this process past its MTPD or, short of that, its RTO. */
  incidentBreaches?: IncidentBreach[];
}

type RestoreCommitment =
  | { state: "committed"; restoreTimeHours: number; restorePointHours: number | null; contractTitle: string }
  | { state: "none" } | { state: "unanswered" } | { state: "no_agreement" };
type RecoveryVerdict = "meets" | "short" | "not_comparable" | "unknown";
interface RecoveryCommitments {
  suppliers: Array<{ id: string; name: string; restore: RestoreCommitment; time: RecoveryVerdict; point: RecoveryVerdict }>;
  exercise: { id: string; title: string; kind: string; completedAt: string; recoveryTimeHours: number | null; recoveryPointHours: number | null; time: RecoveryVerdict; point: RecoveryVerdict } | null;
  gaps: ReadinessGap[];
}

const fmtRecoveryHours = (h: number | null) => (h == null ? "not recorded" : Number.isInteger(h) ? hoursLabel(h) : `${Math.round(h * 10) / 10} hours`);

const RESTORE_STATE_LABEL = { none: "agreements make no restore commitment", unanswered: "restore commitment not recorded", no_agreement: "no agreement in force" } as const;

function RestoreCommitmentText({ s }: { s: RecoveryCommitments["suppliers"][number] }) {
  const r = s.restore;
  if (r.state !== "committed") return <VerdictText verdict="unknown">{RESTORE_STATE_LABEL[r.state]}</VerdictText>;
  return (
    <>
      <VerdictText verdict={s.time}>restore within {hoursLabel(r.restoreTimeHours)}</VerdictText>
      {r.restorePointHours != null && <>, <VerdictText verdict={s.point}>data loss up to {hoursLabel(r.restorePointHours)}</VerdictText></>}
      <span style={{ color: theme.color.muted }}> ({r.contractTitle})</span>
    </>
  );
}

function VerdictText({ verdict, children }: { verdict: RecoveryVerdict; children: ReactNode }) {
  const color = verdict === "short" ? theme.color.dangerText : verdict === "meets" ? theme.color.successText : theme.color.muted;
  return <span style={{ color, fontWeight: verdict === "short" ? 600 : 400 }}>{children}</span>;
}

interface IncidentBreach {
  incidentId: string;
  ref: string;
  title: string;
  status: string;
  hours: number;
  basis: "recorded" | "resolved" | "ongoing";
  exceeded: "mtpd" | "rto";
  /** The incident's assets through which this analysis is affected. */
  via: string[];
}

const BASIS_LABEL: Record<IncidentBreach["basis"], string> = { recorded: "recorded duration", resolved: "until resolved", ongoing: "still running" };

interface BiaResponse { analyses: Analysis[]; attention: AttentionItem[]; vocabulary: Vocabulary }

interface AssetOption { id: string; name: string; kind: string; status: string }

// ── Helpers ───────────────────────────────────────────────────────────────────

/** "4 hours", "2 days", "1 week" — mirror of lib/businessImpact.ts hoursLabel. */
export function hoursLabel(h: number | null | undefined): string {
  if (h == null) return "—";
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"}`;
  if (h % 168 === 0) return `${h / 168} week${h === 168 ? "" : "s"}`;
  if (h % 24 === 0) return `${h / 24} day${h === 24 ? "" : "s"}`;
  return `${h} hours`;
}

const biaRef = (b: { displayId: number | null }) => (b.displayId != null ? `BIA-${String(b.displayId).padStart(3, "0")}` : "BIA");

const SEVERE = 3;

/** Worst level of a dimension up to an interval, carried forward (lib/businessImpact.ts levelAt). */
function levelAt(m: Matrix, dim: string, interval: string, intervals: Interval[]): number {
  let worst = 0;
  for (const iv of intervals) {
    const v = m[dim]?.[iv.id];
    if (typeof v === "number" && v > worst) worst = v;
    if (iv.id === interval) break;
  }
  return worst;
}

function curveOf(m: Matrix, vocab: Vocabulary) {
  return vocab.intervals.map((iv) => {
    const levels = vocab.dimensions.map((d) => ({ id: d.id, label: d.label, level: levelAt(m, d.id, iv.id, vocab.intervals) }));
    const level = Math.max(0, ...levels.map((l) => l.level));
    return { interval: iv, level, drivers: level > 0 ? levels.filter((l) => l.level === level).map((l) => l.label) : [] };
  });
}

function suggestedMtpd(m: Matrix, vocab: Vocabulary): number | null {
  return curveOf(m, vocab).find((p) => p.level >= SEVERE)?.interval.hours ?? null;
}

function tierOf(m: Matrix, vocab: Vocabulary, mtpd: number | null): 1 | 2 | 3 | 4 {
  const at = Math.min(suggestedMtpd(m, vocab) ?? Infinity, mtpd ?? Infinity);
  if (at <= 4) return 1;
  if (at <= 24) return 2;
  if (at <= 168) return 3;
  return 4;
}

const LEVEL_STYLE: Array<{ bg: string; color: string }> = [
  { bg: theme.color.panelAlt,   color: theme.color.muted },
  { bg: theme.color.riskLowBg,  color: theme.color.riskLowText },
  { bg: theme.color.riskMedBg,  color: theme.color.riskMedText },
  { bg: theme.color.riskHighBg, color: theme.color.riskHighText },
  { bg: theme.color.riskCritBg, color: theme.color.riskCritText },
];

const TIER_STYLE: Record<number, { bg: string; color: string }> = {
  1: { bg: theme.color.dangerBg,  color: theme.color.dangerText },
  2: { bg: theme.color.warningBg, color: theme.color.warningText },
  3: { bg: theme.color.accentBg,  color: theme.color.accentText },
  4: { bg: theme.color.panelAlt,  color: theme.color.muted },
};

const pill = (bg: string, color: string): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: bg, color, whiteSpace: "nowrap" });

function TierPill({ tier, title }: { tier: number; title?: string }) {
  const s = TIER_STYLE[tier] ?? TIER_STYLE[4];
  return <span title={title} style={pill(s.bg, s.color)}>Tier {tier}</span>;
}

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  draft:    { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Draft" },
  approved: { bg: theme.color.successBg, color: theme.color.successText, label: "Approved" },
  archived: { bg: theme.color.panelAlt,  color: theme.color.faint,       label: "Archived" },
};

function BiaStatusPill({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.draft;
  return <span style={pill(s.bg, s.color)}>{s.label}</span>;
}

function ReadinessBar({ score }: { score: number }) {
  const color = score >= 80 ? "var(--c-success)" : score >= 50 ? "var(--c-warning)" : "var(--c-danger)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }} title={`Readiness ${score}%`}>
      <RulerProgress value={Math.max(0, Math.min(100, score))} fill={color} label="Readiness" style={{ width: 64 }} />
      <span style={text.hint}>{score}%</span>
    </div>
  );
}

const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

// ── Tab ───────────────────────────────────────────────────────────────────────

export function BiaTab({ canEdit, onChanged }: Props) {
  const { user } = useCurrentUser();
  const [data, setData] = useState<BiaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<Option[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [suppliers, setSuppliers] = useState<Option[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  const openId = params.get("bia");

  const load = useCallback(async () => {
    try {
      setData(await api.get<BiaResponse>("/business-impact"));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the business impact analyses"));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    (async () => {
      const [m, a, s] = await Promise.all([
        // Only members who can edit resilience records can own, and so approve, an analysis (RES-18).
        api.get<{ owners: Option[] }>("/business-impact/owners").then((d) => d.owners).catch(() => [] as Option[]),
        api.get<{ assets: AssetOption[] }>("/resilience/assets").then((d) => d.assets).catch(() => [] as AssetOption[]),
        // The supplier register is the TPRM add-on: when it is off the call is refused and no picker is shown.
        api.get<{ suppliers: Option[] }>("/tprm/suppliers").then((d) => d.suppliers.map((x) => ({ id: x.id, name: x.name }))).catch(() => null),
      ]);
      setMembers(m); setAssets(a); setSuppliers(s);
    })();
  }, []);

  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("bia", id); else next.delete("bia");
    setParams(next, { replace: true });
  };

  async function changed() { await load(); onChanged(); }

  if (!data) return error ? <div style={{ ...text.body, ...card, padding: 14, color: theme.color.dangerText }}>{error}</div> : <LoadingBlock />;
  const { analyses, attention, vocabulary } = data;
  const live = analyses.filter((a) => a.status !== "archived");
  const tier1 = live.filter((a) => a.derived.tier === 1).length;
  const tier2 = live.filter((a) => a.derived.tier === 2).length;
  const avgReadiness = live.length ? Math.round(live.reduce((n, a) => n + a.derived.readiness.score, 0) / live.length) : null;
  const highGaps = live.reduce((n, a) => n + a.derived.readiness.gaps.filter((g) => g.severity === "high").length, 0);
  const open = openId ? analyses.find((a) => a.id === openId) ?? null : null;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PlaybookBar ids={["business-continuity"]} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <p style={{ ...pageSubtitle, maxWidth: 760 }}>
          For each business process or service, record how badly its loss hurts as an outage grows, then set how long it can be down and how fast it must come back. The platform checks that everything it rests on can recover in time (ISO 22301 8.2.2, ISO 27001 A.5.29–A.5.30, NCSC CAF A1.a / B5.a). For an operator of essential services, this is how long an essential service can be down.
        </p>
        {canEdit && <button type="button" style={btnPrimary} onClick={() => setCreating(true)}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />New analysis</button>}
      </div>
      {error && <div style={{ ...text.body, ...card, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        {([
          ["Processes analysed", live.length, `${analyses.filter((a) => a.status === "approved").length} approved`],
          ["Tier 1 / tier 2", `${tier1} / ${tier2}`, "severe within 4 hours / a day"],
          ["Average readiness", avgReadiness != null ? `${avgReadiness}%` : "—", "recovery checks that pass"],
          ["Cannot recover in time", highGaps, highGaps === 1 ? "dependency slower than the RTO" : "dependencies slower than the RTO"],
        ] as Array<[string, string | number, string]>).map(([label, value, sub]) => (
          <div key={label} style={{ ...card, padding: "14px 16px" }}>
            <div style={text.statLabel}>{label}</div>
            <div style={{ ...text.stat, marginTop: 4 }}>{value}</div>
            <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {attention.length > 0 && (
        <div style={{ ...card, padding: 0 }}>
          <div style={{ ...text.sectionTitle, margin: 0, padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={15} /> Needs attention
            <span style={{ ...text.hint, fontWeight: 400 }}>{attention.length} item{attention.length === 1 ? "" : "s"}</span>
          </div>
          {[...attention].sort((a, b) => "hml".indexOf(a.severity[0]) - "hml".indexOf(b.severity[0])).map((a) => (
            <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
              <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={text.itemTitle}>{a.title}</div>
                  <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                </div>
                {a.dueAt && <span style={{ ...text.meta, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
              </div>
            </PortalLink>
          ))}
        </div>
      )}

      {analyses.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 0 }}>
          <EmptyState
            title="No business impact analyses yet"
            detail="Start with the service you could least afford to lose. Score how the harm grows over the first hours, days and weeks, set the recovery objectives, and name the assets and suppliers it rests on. The platform then checks each of them can recover in time."
            action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setCreating(true)}>Analyse the first process</button> : undefined}
          />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={tableHeadRow}>
              {["Ref", "Process or service", "Owner", "Tier", "MTPD", "RTO", "RPO", "Readiness", "Status", "Next review"].map((h) => <th key={h} style={{ padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {analyses.map((a) => (
                <tr key={a.id} onClick={() => setOpen(a.id)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer", opacity: a.status === "archived" ? 0.6 : 1 }}>
                  <td style={{ ...text.ref, padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{biaRef(a)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.text, fontWeight: 600 }}>
                    {a.name}{a.serviceAsset && <div style={{ ...text.meta, fontWeight: 400 }}>{a.serviceAsset.name}</div>}
                    {(a.incidentBreaches?.length ?? 0) > 0 && (
                      <div style={{ ...text.meta, fontWeight: 600, color: theme.color.dangerText }}>
                        Past its tolerance in {a.incidentBreaches!.length} incident{a.incidentBreaches!.length === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted }}>{ownerOf(a)}</td>
                  <td style={{ padding: "10px 14px" }}><TierPill tier={a.derived.tier} title={vocabulary.tiers[String(a.derived.tier)]} /></td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{hoursLabel(a.mtpdHours)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{hoursLabel(a.rtoHours)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{hoursLabel(a.rpoHours)}</td>
                  <td style={{ padding: "10px 14px" }}><ReadinessBar score={a.derived.readiness.score} /></td>
                  <td style={{ padding: "10px 14px" }}><BiaStatusPill status={a.status} /></td>
                  <td style={{ padding: "10px 14px", color: a.nextReviewAt && new Date(a.nextReviewAt).getTime() < Date.now() ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{fmtDay(a.nextReviewAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <BiaDetail
          key={open.id} analysis={open} vocab={vocabulary} canEdit={canEdit} userId={user?.id ?? null}
          members={members} assets={assets} suppliers={suppliers}
          onClose={() => setOpen(null)} onChanged={changed} onDeleted={async () => { setOpen(null); await changed(); }}
        />
      )}
      {openId && !open && analyses.length > 0 && (
        <ModalOverlay onClose={() => setOpen(null)}>
          <ModalHeader title="Analysis not found" onClose={() => setOpen(null)} />
          <div style={{ ...text.body, padding: 20, color: theme.color.muted }}>That analysis no longer exists or is not in this organisation.</div>
        </ModalOverlay>
      )}
      {creating && (
        <ModalOverlay wide onClose={() => setCreating(false)}>
          <ModalHeader title="New business impact analysis" onClose={() => setCreating(false)} />
          <BiaEditor
            existing={null} vocab={vocabulary} members={members} assets={assets} suppliers={suppliers}
            onCancel={() => setCreating(false)}
            onSaved={async (id) => { setCreating(false); await changed(); setOpen(id); }}
          />
        </ModalOverlay>
      )}
    </div>
  );
}

const ownerOf = (a: Analysis) => a.owner?.displayName || a.owner?.email || a.ownerName || "—";

// ── Detail ────────────────────────────────────────────────────────────────────

/**
 * What a complete analysis holds (ISO 22317, ISO 22301 8.2.2): an owner, the impacts over time,
 * the tolerances that follow from them (MTPD, RTO, RPO, minimum service), what it depends on,
 * the workarounds and people, an approval and a review date.
 */
function biaSteps(a: Analysis): NextStepItem[] {
  if (a.status === "archived") return [];
  const assessed = Object.values(a.impacts ?? {}).some((row) => Object.values(row ?? {}).some((v) => typeof v === "number" && v > 0));
  const reviewOverdue = !!a.nextReviewAt && new Date(a.nextReviewAt).getTime() < Date.now();
  const highGaps = a.derived.readiness.gaps.filter((g) => g.severity === "high").length;
  const recoveryGaps = (a.derived.recovery?.gaps ?? []).filter((g) => g.severity !== "low");
  return [
    { done: !!(a.ownerUserId || a.ownerName), label: "Name the process owner" },
    { done: assessed, label: "Rate the impact of an outage over time", hint: "Each dimension, at each interval: this is what sets the tolerances." },
    { done: a.mtpdHours != null, label: "Set the maximum tolerable period of disruption (MTPD)" },
    { done: a.rtoHours != null, label: "Set the recovery time objective (RTO)", hint: a.derived.toleranceIssues[0]?.message, urgent: a.derived.toleranceIssues.length > 0 },
    { done: a.rpoHours != null, label: "Set the recovery point objective (RPO)" },
    { done: !!a.minimumServiceLevel?.trim(), label: "Describe the minimum level of service" },
    { done: a.assets.length > 0 || a.suppliers.length > 0 || !!a.serviceAssetId, label: "Link the assets and suppliers it depends on" },
    { done: !!a.workarounds?.trim(), label: "Record the workarounds while it is down" },
    { done: !!a.keyPeople?.trim(), label: "Name the key people for recovery" },
    ...(a.derived.readiness.gaps.length > 0 ? [{ done: false, label: "Close the recovery readiness gaps", hint: `${a.derived.readiness.gaps.length} gap${a.derived.readiness.gaps.length === 1 ? "" : "s"} below, ${highGaps} high.`, urgent: highGaps > 0 }] : []),
    ...(recoveryGaps.length > 0 ? [{ done: false, label: "Bring supplier commitments and restore tests within the objectives", hint: recoveryGaps[0].message, urgent: recoveryGaps.some((g) => g.severity === "high") }] : []),
    { done: a.status === "approved", label: "Have the owner approve it" },
    ...(a.status === "approved" ? [{ done: !reviewOverdue, label: "Review it (the review date has passed)", urgent: reviewOverdue }] : []),
  ];
}

function BiaDetail({ analysis: a, vocab, canEdit, userId, members, assets, suppliers, onClose, onChanged, onDeleted }: {
  analysis: Analysis; vocab: Vocabulary; canEdit: boolean; userId: string | null; members: Option[]; assets: AssetOption[]; suppliers: Option[] | null;
  onClose: () => void; onChanged: () => Promise<void>; onDeleted: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const r = a.derived.readiness;

  async function setStatus(status: "approved" | "archived" | "draft") {
    setBusy(true); setError(null);
    try { await api.post(`/business-impact/${a.id}/status`, { status }); await onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "Could not change the status")); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(`Delete ${biaRef(a)} "${a.name}"? This cannot be undone.`)) return;
    setBusy(true); setError(null);
    try { await api.del(`/business-impact/${a.id}`); await onDeleted(); }
    catch (e) { setError(apiErrorMessage(e, "Could not delete the analysis")); setBusy(false); }
  }

  const title = <span><span style={{ ...text.ref, fontSize: "inherit", color: theme.color.muted }}>{biaRef(a)}</span> · {a.name}</span>;
  if (editing) {
    return (
      <ModalOverlay wide onClose={onClose}>
        <ModalHeader title={<>Edit {biaRef(a)}</>} onClose={onClose} />
        <BiaEditor existing={a} vocab={vocab} members={members} assets={assets} suppliers={suppliers} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await onChanged(); }} />
      </ModalOverlay>
    );
  }

  const ownerMayApprove = !a.ownerUserId || a.ownerUserId === userId;

  return (
    <ModalOverlay wide onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} right={<><TierPill tier={a.derived.tier} title={vocab.tiers[String(a.derived.tier)]} /><BiaStatusPill status={a.status} />{canEdit && <EditButton onClick={() => setEditing(true)} />}</>} />
      <div style={{ padding: 20, display: "grid", gap: 20 }}>
        <EntityTagsRow entity={{ kind: "bia", id: a.id }} readOnly={!canEdit} />

        <NextSteps items={biaSteps(a)} action={canEdit ? <button type="button" style={secondaryBtn} onClick={() => setEditing(true)}>Fill these in</button> : undefined} />

        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}

        {a.description && <div style={{ ...text.body, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{a.description}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {([
            ["Readiness", `${r.score}%`, `${r.passed} of ${r.checks} checks pass`],
            ["Max tolerable period", hoursLabel(a.mtpdHours), a.derived.suggestedMtpdHours != null ? `impacts severe at ${hoursLabel(a.derived.suggestedMtpdHours)}` : "never severe in 4 weeks"],
            ["Recovery time (RTO)", hoursLabel(a.rtoHours), r.slowestRecoveryHours != null ? `slowest dependency ${hoursLabel(r.slowestRecoveryHours)}` : "slowest dependency unknown"],
            ["Recovery point (RPO)", hoursLabel(a.rpoHours), "data loss tolerated"],
          ] as Array<[string, string, string]>).map(([label, value, sub]) => (
            <div key={label} style={{ ...card, padding: "12px 14px" }}>
              <div style={text.statLabel}>{label}</div>
              <div style={{ ...text.stat, marginTop: 4 }}>{value}</div>
              <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>

        {a.derived.toleranceIssues.length > 0 && (
          <div style={{ ...text.body, padding: "10px 14px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText, border: "1px solid var(--c-warning)" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>The tolerances do not add up</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>{a.derived.toleranceIssues.map((t, i) => <li key={i}>{t.message}</li>)}</ul>
          </div>
        )}

        <DetailSection title="Impact over time">
          <ImpactMatrix matrix={a.impacts} vocab={vocab} />
          <ImpactCurve matrix={a.impacts} vocab={vocab} />
        </DetailSection>

        <DetailSection title="Recovery readiness">
          {r.gaps.length === 0 ? (
            <div style={{ ...text.body, color: theme.color.successText }}>Every check passes: each dependency can recover within the RTO, has a tested plan, and the process has been exercised in the last twelve months.</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {r.gaps.map((g, i) => (
                <div key={i} style={{ ...text.body, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <SeverityPill value={g.severity} />
                  <span style={{ lineHeight: 1.5 }}>{g.message}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ ...text.hint, marginTop: 8 }}>Last exercised: {a.derived.lastExercisedAt ? fmtDay(a.derived.lastExercisedAt) : "no completed exercise covers these assets"}</div>
        </DetailSection>

        {a.derived.recovery && (a.derived.recovery.suppliers.length > 0 || a.derived.recovery.exercise) && (
          <DetailSection title="Recovery commitments">
            <div style={{ ...text.hint, marginBottom: 8 }}>What the suppliers' agreements in force commit to, and what the last restore test achieved, against the RTO ({hoursLabel(a.rtoHours)}) and RPO ({hoursLabel(a.rpoHours)}). Nothing here changes the objectives or the readiness score.</div>
            <div style={{ display: "grid", gap: 6 }}>
              {a.derived.recovery.suppliers.map((s) => (
                <div key={s.id} style={{ ...text.body, lineHeight: 1.5 }}>
                  <PortalLink to={`/tprm?supplier=${s.id}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>{s.name}</PortalLink>
                  : <RestoreCommitmentText s={s} />
                </div>
              ))}
              {a.derived.recovery.exercise && (
                <div style={{ ...text.body, lineHeight: 1.5 }}>
                  <PortalLink to={`/resilience/exercises?exercise=${encodeURIComponent(a.derived.recovery.exercise.id)}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>{a.derived.recovery.exercise.title}</PortalLink>
                  {` (${fmtDay(a.derived.recovery.exercise.completedAt)}): `}
                  <VerdictText verdict={a.derived.recovery.exercise.time}>restored in {fmtRecoveryHours(a.derived.recovery.exercise.recoveryTimeHours)}</VerdictText>
                  {", "}
                  <VerdictText verdict={a.derived.recovery.exercise.point}>data {a.derived.recovery.exercise.recoveryPointHours != null ? `${fmtRecoveryHours(a.derived.recovery.exercise.recoveryPointHours)} behind` : "loss not recorded"}</VerdictText>
                </div>
              )}
            </div>
            {a.derived.recovery.gaps.length > 0 && (
              <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                {a.derived.recovery.gaps.map((g, i) => (
                  <div key={i} style={{ ...text.body, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <SeverityPill value={g.severity} />
                    <span style={{ lineHeight: 1.5 }}>{g.message}</span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>
        )}

        {(a.incidentBreaches?.length ?? 0) > 0 && (
          <DetailSection title="Incidents past its tolerance">
            <div style={{ display: "grid", gap: 6 }}>
              {a.incidentBreaches!.map((b) => (
                <div key={b.incidentId} style={{ ...text.body, lineHeight: 1.5 }}>
                  <PortalLink to={`/resilience/incidents?incident=${encodeURIComponent(b.incidentId)}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>
                    <span style={text.ref}>{b.ref}</span> {b.title}
                  </PortalLink>
                  : {hoursLabel(b.hours)}, <span style={{ color: b.exceeded === "mtpd" ? theme.color.dangerText : theme.color.warningText, fontWeight: 600 }}>past the {b.exceeded === "mtpd" ? "MTPD" : "RTO"}</span> ({BASIS_LABEL[b.basis]})
                  {b.via.length > 0 && <div style={text.meta}>Through {b.via.join(", ")}</div>}
                </div>
              ))}
            </div>
          </DetailSection>
        )}

        <DetailSection title="Dependencies">
          {a.derived.dependencies.length === 0 && a.suppliers.length === 0 ? (
            <div style={{ ...text.body, color: theme.color.muted }}>None named. Add the assets and suppliers this process rests on so recovery can be checked.</div>
          ) : (
            <div style={{ ...card, padding: 0, overflowX: "auto" }}>
              <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={tableHeadRow}>
                  {["Dependency", "How", "Recovers in", "Contingency"].map((h) => <th key={h} style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {a.derived.dependencies.map((d) => {
                    const slow = a.rtoHours != null && d.rtoHours != null && d.rtoHours > a.rtoHours;
                    return (
                      <tr key={d.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                        <td style={{ padding: "8px 12px", color: theme.color.text }}>
                          <PortalLink to={`/resilience/assets?asset=${d.id}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>{d.name}</PortalLink>
                          {d.id === a.serviceAssetId && <span style={text.meta}> (the service itself)</span>}
                          {d.status === "retired" && <span style={{ ...text.meta, color: theme.color.dangerText }}> retired</span>}
                        </td>
                        <td style={{ padding: "8px 12px", color: theme.color.muted, whiteSpace: "nowrap" }}>{d.depth === 0 ? "direct" : `via ${d.depth} hop${d.depth === 1 ? "" : "s"}`}</td>
                        <td style={{ padding: "8px 12px", color: slow ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap", fontWeight: slow ? 600 : 400 }}>{d.rtoHours != null ? hoursLabel(d.rtoHours) : "not recorded"}</td>
                        <td style={{ padding: "8px 12px", color: theme.color.muted }}>{d.depth > 0 ? "—" : !d.contingencyPlan ? "no plan" : d.contingencyTestedAt ? `tested ${fmtDay(d.contingencyTestedAt)}` : "plan, never tested"}</td>
                      </tr>
                    );
                  })}
                  {a.suppliers.map((s) => {
                    const rc = a.derived.recovery?.suppliers.find((x) => x.id === s.supplierId);
                    return (
                    <tr key={s.supplierId} style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ padding: "8px 12px", color: theme.color.text }}>
                        <PortalLink to={`/tprm?supplier=${s.supplierId}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>{s.supplier.name}</PortalLink>
                      </td>
                      <td style={{ padding: "8px 12px", color: theme.color.muted }}>supplier</td>
                      <td style={{ padding: "8px 12px", color: rc?.time === "short" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap", fontWeight: rc?.time === "short" ? 600 : 400 }}>
                        {rc?.restore.state === "committed" ? `${hoursLabel(rc.restore.restoreTimeHours)} (contracted)` : rc?.restore.state === "none" ? "no commitment" : rc?.restore.state === "unanswered" ? "not recorded" : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", color: theme.color.muted }}>{s.supplier.status}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DetailSection>

        <DetailSection title="Details">
          <DetailFields fields={[
            { label: "Owner", value: ownerOf(a) === "—" ? null : ownerOf(a) },
            { label: "Business service", value: a.serviceAsset?.name },
            { label: "Minimum service level", value: a.minimumServiceLevel, multiline: true },
            { label: "Loss per hour", value: a.financialLossPerHour != null ? fmtMoney(a.financialLossPerHour, a.currency) : null },
            { label: "Peak periods", value: a.peakPeriods, multiline: true },
            { label: "Workarounds", value: a.workarounds ? `${a.workarounds}${a.workaroundMaxHours != null ? ` (sustainable for ${hoursLabel(a.workaroundMaxHours)})` : ""}` : null, wide: true, multiline: true },
            { label: "Key people", value: a.keyPeople, multiline: true },
            { label: "Regulatory notes", value: a.regulatoryNotes, multiline: true },
            { label: "Review", value: `Every ${a.reviewIntervalMonths} months · last ${fmtDay(a.lastReviewedAt)} · next ${fmtDay(a.nextReviewAt)}` },
            { label: "Approved", value: a.approvedAt ? fmtDay(a.approvedAt) : "Not yet" },
          ]} />
        </DetailSection>

        {canEdit && (
          <RecordActions note={a.status === "draft" && a.ownerUserId && a.ownerUserId !== userId ? `Only ${ownerOf(a)}, the named owner, can approve this analysis.` : undefined}>
            {a.status !== "approved" && a.status !== "archived" && (
              <button type="button" style={secondaryBtn} disabled={busy || !ownerMayApprove} onClick={() => setStatus("approved")} title={ownerMayApprove ? "Approve the objectives as the owner's commitment" : "Only the named owner can approve"}>Approve</button>
            )}
            {a.status !== "archived" && <button type="button" style={secondaryBtn} disabled={busy} onClick={() => setStatus("archived")}>Archive</button>}
            {a.status !== "draft" && <button type="button" style={secondaryBtn} disabled={busy} onClick={() => setStatus("draft")}>Reopen as draft</button>}
            <button type="button" style={dangerBtn} disabled={busy} onClick={remove}>Delete analysis</button>
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

function fmtMoney(v: number, currency: string): string {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 0 }).format(v); }
  catch { return `${currency} ${Math.round(v).toLocaleString("en-GB")}`; }
}

// ── Impact matrix and curve ───────────────────────────────────────────────────

function ImpactMatrix({ matrix, vocab, onChange }: { matrix: Matrix; vocab: Vocabulary; onChange?: (m: Matrix) => void }) {
  const set = (dim: string, iv: string, v: number) => {
    if (!onChange) return;
    onChange({ ...matrix, [dim]: { ...(matrix[dim] ?? {}), [iv]: v } });
  };
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ ...text.hint, width: "100%", borderCollapse: "collapse" }}>
        <thead><tr style={tableHeadRow}>
          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>Impact</th>
          {vocab.intervals.map((iv) => <th key={iv.id} style={{ padding: "8px 6px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, whiteSpace: "nowrap", textAlign: "center" }}>{iv.label}</th>)}
        </tr></thead>
        <tbody>
          {vocab.dimensions.map((d) => (
            <tr key={d.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
              <td style={{ padding: "8px 12px", minWidth: 160 }}>
                <div style={text.itemTitle}>{d.label}</div>
                <div style={text.meta}>{d.help}</div>
              </td>
              {vocab.intervals.map((iv) => {
                const raw = matrix[d.id]?.[iv.id];
                const shown = typeof raw === "number" ? raw : 0;
                const s = LEVEL_STYLE[shown] ?? LEVEL_STYLE[0];
                const label = vocab.levels.find((l) => l.level === shown)?.label ?? String(shown);
                return (
                  <td key={iv.id} style={{ padding: "4px 4px", textAlign: "center" }}>
                    {onChange ? (
                      <select
                        aria-label={`${d.label} at ${iv.label}`}
                        value={shown}
                        onChange={(e) => set(d.id, iv.id, Number(e.target.value))}
                        style={{ width: "100%", minWidth: 86, fontSize: text.hint.fontSize, padding: "5px 4px", borderRadius: 3, border: "1px solid var(--c-border)", background: s.bg, color: s.color, fontWeight: shown >= SEVERE ? 700 : 500, cursor: "pointer" }}
                      >
                        {vocab.levels.map((l) => <option key={l.level} value={l.level}>{l.label}</option>)}
                      </select>
                    ) : (
                      <div style={{ padding: "5px 4px", borderRadius: 3, background: s.bg, color: s.color, fontWeight: shown >= SEVERE ? 700 : 500 }}>{label}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImpactCurve({ matrix, vocab }: { matrix: Matrix; vocab: Vocabulary }) {
  const curve = curveOf(matrix, vocab);
  const severeAt = suggestedMtpd(matrix, vocab);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ ...text.label, marginBottom: 6 }}>Impact curve (worst impact so far)</div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${curve.length}, minmax(0, 1fr))`, gap: 4 }}>
        {curve.map((p) => {
          const s = LEVEL_STYLE[p.level] ?? LEVEL_STYLE[0];
          const label = vocab.levels.find((l) => l.level === p.level)?.label ?? String(p.level);
          return (
            <div key={p.interval.id} title={p.drivers.length ? `Driven by ${p.drivers.join(", ")}` : undefined} style={{ padding: "6px 4px", borderRadius: 3, background: s.bg, color: s.color, textAlign: "center", fontSize: text.meta.fontSize, minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{label}</div>
              <div style={{ opacity: 0.85 }}>{p.interval.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ ...text.hint, marginTop: 6 }}>
        {severeAt != null
          ? <>Becomes severe at <strong style={{ color: theme.color.text }}>{hoursLabel(severeAt)}</strong> — the suggested maximum tolerable period.</>
          : "Never reaches severe within four weeks on these scores."}
      </div>
    </div>
  );
}

// ── Editor ────────────────────────────────────────────────────────────────────

const UNITS: Array<{ id: number; label: string }> = [{ id: 1, label: "hours" }, { id: 24, label: "days" }, { id: 168, label: "weeks" }];
interface HoursValue { v: string; unit: number }

function toHoursValue(h: number | null): HoursValue {
  if (h == null) return { v: "", unit: 1 };
  if (h > 0 && h % 168 === 0) return { v: String(h / 168), unit: 168 };
  if (h > 0 && h % 24 === 0) return { v: String(h / 24), unit: 24 };
  return { v: String(h), unit: 1 };
}
const fromHoursValue = (x: HoursValue): number | null => (x.v.trim() === "" ? null : Math.round(Number(x.v) * x.unit));

function HoursInput({ value, onChange }: { value: HoursValue; onChange: (v: HoursValue) => void }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <input type="number" min={0} step="any" value={value.v} onChange={(e) => onChange({ ...value, v: e.target.value })} style={{ ...input, flex: 1, minWidth: 0 }} />
      <select value={value.unit} onChange={(e) => onChange({ ...value, unit: Number(e.target.value) })} style={{ ...input, width: 92 }}>
        {UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
      </select>
    </div>
  );
}

interface Form {
  name: string; description: string; ownerUserId: string; ownerName: string; serviceAssetId: string; impacts: Matrix;
  financialLossPerHour: string; currency: string; mtpd: HoursValue; rto: HoursValue; rpo: HoursValue;
  minimumServiceLevel: string; peakPeriods: string; workarounds: string; workaroundMax: HoursValue; keyPeople: string; regulatoryNotes: string;
  reviewIntervalMonths: number; assetIds: string[]; supplierIds: string[];
}

function toForm(a: Analysis | null): Form {
  return {
    name: a?.name ?? "", description: a?.description ?? "", ownerUserId: a?.ownerUserId ?? "", ownerName: a?.ownerName ?? "", serviceAssetId: a?.serviceAssetId ?? "",
    impacts: a?.impacts ?? {}, financialLossPerHour: a?.financialLossPerHour?.toString() ?? "", currency: a?.currency ?? "GBP",
    mtpd: toHoursValue(a?.mtpdHours ?? null), rto: toHoursValue(a?.rtoHours ?? null), rpo: toHoursValue(a?.rpoHours ?? null),
    minimumServiceLevel: a?.minimumServiceLevel ?? "", peakPeriods: a?.peakPeriods ?? "", workarounds: a?.workarounds ?? "", workaroundMax: toHoursValue(a?.workaroundMaxHours ?? null),
    keyPeople: a?.keyPeople ?? "", regulatoryNotes: a?.regulatoryNotes ?? "", reviewIntervalMonths: a?.reviewIntervalMonths ?? 12,
    assetIds: a?.assets.map((x) => x.assetId) ?? [], supplierIds: a?.suppliers.map((x) => x.supplierId) ?? [],
  };
}

function toPayload(f: Form) {
  const t = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    name: f.name.trim(), description: t(f.description), ownerUserId: f.ownerUserId || null, ownerName: f.ownerUserId ? null : t(f.ownerName),
    serviceAssetId: f.serviceAssetId || null, impacts: f.impacts,
    financialLossPerHour: f.financialLossPerHour.trim() === "" ? null : Number(f.financialLossPerHour), currency: (f.currency.trim() || "GBP").toUpperCase(),
    mtpdHours: fromHoursValue(f.mtpd), rtoHours: fromHoursValue(f.rto), rpoHours: fromHoursValue(f.rpo),
    minimumServiceLevel: t(f.minimumServiceLevel), peakPeriods: t(f.peakPeriods), workarounds: t(f.workarounds), workaroundMaxHours: fromHoursValue(f.workaroundMax),
    keyPeople: t(f.keyPeople), regulatoryNotes: t(f.regulatoryNotes), reviewIntervalMonths: f.reviewIntervalMonths,
    assetIds: f.assetIds, supplierIds: f.supplierIds,
  };
}

/** Stable JSON for comparing a field before and after (key order of a matrix does not matter). */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v as object).sort().map((k) => `${k}:${stable((v as Record<string, unknown>)[k])}`).join(",")}}`;
  return JSON.stringify(v ?? null);
}

function BiaEditor({ existing, vocab, members, assets, suppliers, onCancel, onSaved }: {
  existing: Analysis | null; vocab: Vocabulary; members: Option[]; assets: AssetOption[]; suppliers: Option[] | null;
  onCancel: () => void; onSaved: (id: string) => Promise<void>;
}) {
  const initial = useMemo(() => toForm(existing), [existing]);
  const [f, setF] = useState<Form>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => ({ ...p, [k]: v }));

  const activeAssets = assets.filter((a) => a.status !== "retired");
  const services = activeAssets.filter((a) => a.kind === "business_service");
  const others = activeAssets.filter((a) => a.kind !== "business_service");
  const assetOptions: Option[] = activeAssets.map((a) => ({ id: a.id, name: a.name }));
  // Keep a linked asset that has since been retired visible so it can be unticked.
  for (const x of existing?.assets ?? []) if (!assetOptions.some((o) => o.id === x.assetId)) assetOptions.push({ id: x.assetId, name: x.asset.name });

  const mtpdHours = fromHoursValue(f.mtpd);
  const rtoHours = fromHoursValue(f.rto);
  const suggested = suggestedMtpd(f.impacts, vocab);
  const tier = tierOf(f.impacts, vocab, mtpdHours);
  const ceiling = mtpdHours ?? suggested;

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      const payload = toPayload(f);
      let id: string;
      if (existing) {
        // Send only what changed: any change to impacts, objectives or dependencies returns an
        // approved analysis to draft, so an untouched field must not look like an edit.
        const before = toPayload(initial) as Record<string, unknown>;
        const diff = Object.fromEntries(Object.entries(payload).filter(([k, v]) => stable(v) !== stable(before[k])));
        if (Object.keys(diff).length) await api.put(`/business-impact/${existing.id}`, diff);
        id = existing.id;
      } else {
        const r = await api.post<{ analysis: { id: string } }>("/business-impact", payload);
        id = r.analysis.id;
      }
      await onSaved(id);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the analysis")); setSaving(false);
    }
  }

  const sectionTitle: React.CSSProperties = { ...text.eyebrow, gridColumn: "1 / -1", marginTop: 6 };

  return (
    <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
      <Field label="Process or service *" span2><input required maxLength={200} value={f.name} onChange={(e) => set("name", e.target.value)} style={input} placeholder="e.g. Customer payments" /></Field>
      <Field label="What it does" span2><textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} style={textarea} /></Field>
      <Field label="Owner" help="The owner approves the analysis, so the objectives are their commitment.">
        <select value={f.ownerUserId} onChange={(e) => set("ownerUserId", e.target.value)} style={input}>
          <option value="">Not a member</option>
          {f.ownerUserId && !members.some((m) => m.id === f.ownerUserId) && <option value={f.ownerUserId}>{existing ? ownerOf(existing) : "Current owner"} (cannot edit resilience records)</option>}
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </Field>
      {!f.ownerUserId ? (
        <Field label="Owner name"><input value={f.ownerName} onChange={(e) => set("ownerName", e.target.value)} style={input} /></Field>
      ) : <div />}
      <Field label="Business service in the asset register" span2 help="The asset this analysis is about, when there is one.">
        <select value={f.serviceAssetId} onChange={(e) => set("serviceAssetId", e.target.value)} style={input}>
          <option value="">None</option>
          {services.length > 0 && <optgroup label="Business services">{services.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
          {others.length > 0 && <optgroup label="Other assets">{others.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
          {existing?.serviceAsset && !activeAssets.some((a) => a.id === existing.serviceAsset!.id) && <option value={existing.serviceAsset.id}>{existing.serviceAsset.name}</option>}
        </select>
      </Field>

      <div style={sectionTitle}>Impact over time</div>
      <div style={{ ...text.hint, gridColumn: "1 / -1", marginTop: -8 }}>
        Score how bad the harm is if the process stops for each length of time. A blank later cell keeps the worst earlier score: an outage does not get better by lasting longer.
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <ImpactMatrix matrix={f.impacts} vocab={vocab} onChange={(m) => set("impacts", m)} />
        <ImpactCurve matrix={f.impacts} vocab={vocab} />
        <div style={{ ...text.hint, marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
          On these scores: <TierPill tier={tier} /> <span>{vocab.tiers[String(tier)]}</span>
        </div>
      </div>

      <div style={sectionTitle}>Tolerances</div>
      <Field label="Maximum tolerable period (MTPD)" help={suggested != null ? `Suggested: ${hoursLabel(suggested)}, when the impacts become severe.` : "How long before the harm is unacceptable."}>
        <HoursInput value={f.mtpd} onChange={(v) => set("mtpd", v)} />
        {mtpdHours != null && suggested != null && mtpdHours > suggested && <span style={{ ...text.meta, color: theme.color.warningText }}>Longer than the point the impacts become severe.</span>}
      </Field>
      <Field label="Recovery time objective (RTO)" help="How fast the process must be back. Needed before approval.">
        <HoursInput value={f.rto} onChange={(v) => set("rto", v)} />
        {rtoHours != null && ceiling != null && rtoHours > ceiling && <span style={{ ...text.meta, color: theme.color.warningText }}>Longer than the tolerable period: the service would come back after the damage is done.</span>}
      </Field>
      <Field label="Recovery point objective (RPO)" help="How much data, in time, you can afford to lose.">
        <HoursInput value={f.rpo} onChange={(v) => set("rpo", v)} />
      </Field>
      <Field label="Review every">
        <select value={f.reviewIntervalMonths} onChange={(e) => set("reviewIntervalMonths", Number(e.target.value))} style={input}>
          {[6, 12, 24].map((m) => <option key={m} value={m}>{m} months</option>)}
        </select>
      </Field>
      <Field label="Minimum service level" span2 help="What must keep running, even in a degraded state."><textarea rows={2} value={f.minimumServiceLevel} onChange={(e) => set("minimumServiceLevel", e.target.value)} style={textarea} /></Field>
      <Field label="Loss per hour of outage"><input type="number" min={0} step="any" value={f.financialLossPerHour} onChange={(e) => set("financialLossPerHour", e.target.value)} style={input} /></Field>
      <Field label="Currency"><input maxLength={3} value={f.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} style={input} /></Field>
      <Field label="Peak periods" span2 help="When an outage would hurt most (month end, payroll, trading season)."><input value={f.peakPeriods} onChange={(e) => set("peakPeriods", e.target.value)} style={input} /></Field>
      <Field label="Workarounds" span2><textarea rows={2} value={f.workarounds} onChange={(e) => set("workarounds", e.target.value)} style={textarea} /></Field>
      <Field label="Workaround sustainable for"><HoursInput value={f.workaroundMax} onChange={(v) => set("workaroundMax", v)} /></Field>
      <Field label="Key people"><input value={f.keyPeople} onChange={(e) => set("keyPeople", e.target.value)} style={input} /></Field>
      <Field label="Regulatory notes" span2 help="Licence conditions, regulator expectations, contractual service levels."><textarea rows={2} value={f.regulatoryNotes} onChange={(e) => set("regulatoryNotes", e.target.value)} style={textarea} /></Field>

      <div style={sectionTitle}>Dependencies</div>
      <Field label="Assets it rests on" span2 help="What these depend on in the asset register is followed automatically.">
        <MultiPick options={assetOptions} selected={f.assetIds} onChange={(ids) => set("assetIds", ids)} empty="No assets in the register yet" />
      </Field>
      {suppliers && (
        <Field label="Suppliers it rests on" span2>
          <MultiPick options={suppliers} selected={f.supplierIds} onChange={(ids) => set("supplierIds", ids)} empty="No suppliers in the register yet" />
        </Field>
      )}

      {existing?.status === "approved" && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.warningText }}>Changing the impacts, objectives or dependencies returns this analysis to draft for the owner to approve again.</div>}
      {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
        <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        <button type="button" style={secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
